/**
 * BCP document management service.
 */

import { APIClient } from './api';
import type {
  BCPListItem,
  BCPDocument,
  BCPSaveRequest,
  BCPUpdateRequest,
  BCPListQuery,
  BCPListResponse,
  BCPVersion,
} from '../types/bcp';
import type { APIResponse } from '../types';

const unwrapApiResponse = <T>(
  response: APIResponse<T> | T,
  action: string
): T => {
  if (response && typeof response === 'object' && 'success' in response) {
    const apiResponse = response as APIResponse<T>;
    if (!apiResponse.success) {
      throw new Error(apiResponse.error || `Failed to ${action}`);
    }
    if (apiResponse.data === undefined) {
      throw new Error(`Missing data in ${action} response`);
    }
    return apiResponse.data;
  }

  return response as T;
};

export class BCPService {
  private apiClient: APIClient;
  private baseUrl = '/api/bcp/manage';

  constructor() {
    this.apiClient = new APIClient();
  }

  async listBCPs(params: Partial<BCPListQuery> = {}): Promise<BCPListResponse> {
    const response = await this.apiClient.get<BCPListResponse>(`${this.baseUrl}/list`, { params });
    return unwrapApiResponse(response, 'list BCPs');
  }

  async getBCP(id: string, tenantId?: string, version?: number): Promise<BCPDocument> {
    // tenant_id is automatically injected from session on backend, don't send it
    const params: Record<string, any> = {};
    if (version !== undefined) params.version = version;

    const response = await this.apiClient.get<BCPDocument>(`${this.baseUrl}/${id}`, { params });
    return unwrapApiResponse(response, 'get BCP');
  }

  async saveBCP(request: BCPSaveRequest, tenantId?: string): Promise<BCPDocument> {
    // Include tenant_id in the request body as required by the backend model
    const requestWithTenant = {
      ...request,
      tenant_id: tenantId || 'default'
    };
    
    const params = tenantId ? { tenant_id: tenantId } : {};
    const response = await this.apiClient.post<BCPDocument>(`${this.baseUrl}/save`, requestWithTenant, { params });
    return unwrapApiResponse(response, 'save BCP');
  }

  async updateBCP(id: string, request: BCPUpdateRequest, tenantId?: string, createVersion = true): Promise<BCPDocument> {
    // tenant_id is automatically injected from session on backend, don't send it
    const params: Record<string, any> = { create_version: createVersion };

    const response = await this.apiClient.put<BCPDocument>(`${this.baseUrl}/${id}`, request, { params });
    return unwrapApiResponse(response, 'update BCP');
  }

  async deleteBCP(id: string, tenantId?: string, permanent = false): Promise<void> {
    // tenant_id is automatically injected from session on backend, don't send it
    const params: Record<string, any> = { permanent };

    await this.apiClient.delete(`${this.baseUrl}/${id}`, { params });
  }

  async duplicateBCP(id: string, tenantId?: string): Promise<BCPDocument> {
    // tenant_id is automatically injected from session on backend, don't send it
    const response = await this.apiClient.post<BCPDocument>(`${this.baseUrl}/${id}/duplicate`, {});
    return unwrapApiResponse(response, 'duplicate BCP');
  }

  async exportBCP(id: string, format: 'pdf' | 'docx' = 'pdf', tenantId?: string): Promise<void> {
    // tenant_id is automatically injected from session on backend, don't send it
    const params: Record<string, any> = { format };

    // Use getRaw to access headers and blob data
    const response = await this.apiClient.getRaw(`${this.baseUrl}/${id}/export`, {
      params,
      responseType: 'blob'
    });
    
    // Handle file download
    const blob = new Blob([response.data]);
    const filename = response.headers['content-disposition']?.split('filename=')[1] || `bcp_${id}.${format}`;
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async getVersions(id: string, tenantId?: string): Promise<BCPVersion[]> {
    // tenant_id is automatically injected from session on backend, don't send it
    const response = await this.apiClient.get<BCPVersion[]>(`${this.baseUrl}/${id}/versions`);
    return unwrapApiResponse(response, 'get BCP versions');
  }

  async saveBasicSection(
    bcpId: string | null,
    sectionKey: string,
    sectionData: Record<string, any>,
    progress?: Record<string, any>,
    tenantId?: string,
    status: string = 'draft'
  ): Promise<BCPDocument> {
    const effectiveBcpId = bcpId || 'new';

    // Use postRaw to get unwrapped response (backend returns BCPDocument directly)
    // SECURITY: tenant_id is intentionally excluded from request body.
    // It is automatically injected from the authenticated session on the backend.
    const axiosResponse = await this.apiClient.postRaw<BCPDocument>(
      `/api/bcp/${effectiveBcpId}/basic-section/save`,
      {
        section_key: sectionKey,
        section_data: sectionData,
        progress,
        status
      }
    );

    // Backend returns BCPDocument directly
    return axiosResponse.data;
  }

  async updateBasicSection(
    bcpId: string,
    sectionKey: string,
    sectionData: Record<string, any>,
    progress?: Record<string, any>,
    tenantId?: string
  ): Promise<BCPDocument> {
    return this.saveBasicSection(bcpId, sectionKey, sectionData, progress, tenantId);
  }

  mergeSections(
    existingContent: BCPContent,
    sectionKey: string,
    sectionData: Record<string, any>
  ): BCPContent {
    const content = { ...existingContent };
    if (!content.sections) {
      content.sections = {};
    }
    content.sections[sectionKey] = sectionData;
    return content;
  }
}

// Singleton instance
export const bcpService = new BCPService();
